// Renders one attribute: resolves its kind, picks the component and memoizes it.
// Apps can substitute any kind through the `components` map.

import React from 'react';
import PropTypes from 'prop-types';

import { KINDS } from '../core/fieldTypes';
import CheckboxInput from './fields/CheckboxInput';
import EmailInput from './fields/EmailInput';
import HeadersInput from './fields/HeadersInput';
import JsonPayloadInput from './fields/JsonPayloadInput';
import PasswordInput from './fields/PasswordInput';
import TextAreaInput from './fields/TextAreaInput';
import TextInput from './fields/TextInput';
import { descriptorShape } from './fields/fieldPropTypes';

// Props are flat rather than grouped so React.memo can actually do its job: a
// field only re-renders when its own value or error changes. Grouping the
// callbacks into objects would hand every field a new reference each render,
// which is what stopped the original screens from memoizing at all.
const FieldRenderer = ({
  descriptor,
  kind = null,
  label,
  value = undefined,
  warningMessage = '',
  readOnly = true,
  texts = undefined,
  onChange,
  onCheckboxChange = undefined,
  onBlur = undefined,
  onEmailBlur = undefined,
  headerErrors = {},
  onHeaderAdd = undefined,
  onHeaderRemove = undefined,
  onHeaderChange = undefined,
  onHeaderValidate = undefined,
  jsonEditorOn = false,
  jsonEditorDisabled = false,
  onJsonToggle = undefined,
  onJsonDataChange = undefined,
  jsonEditor = undefined,
  encryptText = undefined,
  maskUntilEdited = false,
  encryptedPlaceholder = '',
  restoreSecureOnErase = false,
  components = {},
}) => {
  const Override = components[kind];

  const common = {
    descriptor,
    label,
    value,
    warningMessage,
    readOnly,
    onChange,
    onBlur,
  };

  if (Override) return <Override {...common} texts={texts} kind={kind} />;

  switch (kind) {
    case KINDS.TEXT:
      return <TextInput {...common} />;
    case KINDS.PASSWORD:
    case KINDS.PASSWORD_HIDDEN:
      return (
        <PasswordInput
          {...common}
          encryptText={encryptText}
          maskUntilEdited={maskUntilEdited}
        />
      );
    case KINDS.CHECKBOX:
      return <CheckboxInput {...common} onChange={onCheckboxChange} />;
    case KINDS.EMAIL:
      return <EmailInput {...common} onBlur={onEmailBlur} />;
    case KINDS.TEXTAREA:
      return <TextAreaInput {...common} />;
    case KINDS.JSON_PAYLOAD:
      return (
        <JsonPayloadInput
          {...common}
          texts={texts}
          editorOn={jsonEditorOn}
          editorDisabled={jsonEditorDisabled}
          onToggle={onJsonToggle}
          onJsonChange={onJsonDataChange}
          jsonEditor={jsonEditor}
        />
      );
    case KINDS.HEADERS:
      return (
        <HeadersInput
          descriptor={descriptor}
          value={value}
          errors={headerErrors}
          readOnly={readOnly}
          texts={texts}
          onChange={onHeaderChange}
          onAdd={onHeaderAdd}
          onRemove={onHeaderRemove}
          onValidate={onHeaderValidate}
          encryptedPlaceholder={encryptedPlaceholder}
          restoreSecureOnErase={restoreSecureOnErase}
        />
      );
    default:
      return null;
  }
};

FieldRenderer.propTypes = {
  descriptor: descriptorShape.isRequired,
  kind: PropTypes.string,
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  warningMessage: PropTypes.string,
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  texts: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func,
  onBlur: PropTypes.func,
  onEmailBlur: PropTypes.func,
  headerErrors: PropTypes.objectOf(PropTypes.string),
  onHeaderAdd: PropTypes.func,
  onHeaderRemove: PropTypes.func,
  onHeaderChange: PropTypes.func,
  onHeaderValidate: PropTypes.func,
  jsonEditorOn: PropTypes.bool,
  jsonEditorDisabled: PropTypes.bool,
  onJsonToggle: PropTypes.func,
  onJsonDataChange: PropTypes.func,
  jsonEditor: PropTypes.elementType,
  encryptText: PropTypes.func,
  maskUntilEdited: PropTypes.bool,
  encryptedPlaceholder: PropTypes.string,
  restoreSecureOnErase: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  components: PropTypes.object,
};
export default React.memo(FieldRenderer);
